import { __assign } from 'tslib';
import React__default, { useEffect } from 'react';
import { addToBasketSelection, selectedElements, dataViewDisplayMode, dataViewMasonryDisplaySize, dataViewSortSelector, downloadManagerSelectionSelector, SpecificDataviewType, useDimensions, LiveMesure, ScrollTriggerDirection, ComponentAugmentKey, ActionValue } from '@keepeek/commons';
import { useTheme, Box } from '@mui/material';
import clsx from 'clsx';
import { useSpring, animated } from 'react-spring';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import ActionsBarLayout from '../../ActionsBar/index.js';
import BusinessFilterToggler from '../../BusinessFilters/BusinessFilterToggler.js';
import Sort from '../../Sort/index.js';
import Actions from '../Actions/index.js';
import { ActionDisplayType } from '../Actions/constants.js';
import ModeSwitcher from '../ModeSwitcher/index.js';
import TotalCount from '../TotalCount/index.js';

var DataviewActionsBarMemo = function (_a) {
    var _b;
    var _c, _d, _e;
    var setSelectionToAdd = _a.setSelectionToAdd, setSelectionToDownloadManager = _a.setSelectionToDownloadManager, setSelectionToContactSheets = _a.setSelectionToContactSheets, setSelectionToInsert = _a.setSelectionToInsert, selected = _a.selected, displayCount = _a.displayCount, sort = _a.sort, setSort = _a.setSort, sortList = _a.sortList, enabledActions = _a.enabledActions, actionButtonsColor = _a.actionButtonsColor, displayModes = _a.displayModes, displayMode = _a.displayMode, setDisplayMode = _a.setDisplayMode, masonryViewSize = _a.masonryViewSize, setMasonryViewSize = _a.setMasonryViewSize, KIcon = _a.KIcon, shareTokenDownloadAuthorized = _a.shareTokenDownloadAuthorized, contactSheetsEnabled = _a.contactSheetsEnabled, allowSelection = _a.allowSelection, _f = _a.sticky, sticky = _f === void 0 ? true : _f, GetCustomerAugmentedComponent = _a.GetCustomerAugmentedComponent, dataViewKey = _a.dataViewKey, displayFilterToggler = _a.displayFilterToggler;
    var _g = useDimensions({
        throttleTime: 0
    }), refDummy = _g.ref, dimensionsDummy = _g.dimensions, scrollDirection = _g.scrollDirection;
    var _h = useDimensions({
        liveMeasure: LiveMesure.Resize
    }), refActionsBar = _h.ref, dimensionsActionsBar = _h.dimensions;
    var theme = useTheme();
    var handleActionClick = function (action) {
        switch (action) {
            case ActionValue.DOWNLOAD:
                setSelectionToDownloadManager(selected);
                break;
            case ActionValue.ADD_TO_BASKET:
                setSelectionToAdd(selected);
                break;
            case ActionValue.CONTACT_SHEETS:
                setSelectionToContactSheets(selected);
                break;
            case ActionValue.INSERT:
                setSelectionToInsert(selected);
                break;
        }
    };
    // Under a size, don't animate the action bar
    var shouldAnimate = theme.breakpoints.values.sm <= ((_c = dimensionsActionsBar === null || dimensionsActionsBar === void 0 ? void 0 : dimensionsActionsBar.width) !== null && _c !== void 0 ? _c : 0);
    var actionsBarHeight = (_d = dimensionsActionsBar === null || dimensionsActionsBar === void 0 ? void 0 : dimensionsActionsBar.height) !== null && _d !== void 0 ? _d : 0;
    var borderWidth = 1;
    var topIspassed = ((_e = dimensionsDummy === null || dimensionsDummy === void 0 ? void 0 : dimensionsDummy.top) !== null && _e !== void 0 ? _e : 1 + actionsBarHeight) < 0;
    var animatedStyles = useSpring({
        top: shouldAnimate && topIspassed && scrollDirection === ScrollTriggerDirection.Down
            ? -(actionsBarHeight + borderWidth)
            : 0,
        borderBottomWidth: shouldAnimate && topIspassed ? borderWidth : 0,
        borderBottomColor: shouldAnimate && topIspassed ? theme.palette.grey[900] : "transparent"
    });
    var ActionBarComponent = (React__default.createElement(GetCustomerAugmentedComponent, { overrideKey: ComponentAugmentKey.ActionsBarLayout, props: {
            displayFilterToggler: displayFilterToggler,
            filterToggler: React__default.createElement(BusinessFilterToggler, { KIcon: KIcon, dataViewKey: dataViewKey }),
            displayCount: displayCount === undefined || displayCount,
            count: (React__default.createElement(TotalCount, { allowSelection: allowSelection, dataViewKey: dataViewKey, KIcon: KIcon })),
            sort: sortList.length > 0 && (React__default.createElement(Sort, { defaultSortValue: sort, onSortChange: setSort, sortList: sortList, sx: {
                    "& .MuiSelect-icon": {
                        display: "none"
                    },
                    "& .MuiListItemIcon-root": {
                        minWidth: "auto"
                    },
                    "&& .MuiInput-input": {
                        paddingRight: "0!important"
                    },
                    "& .MuiSelect-select": (_b = {
                            fontSize: ".8rem"
                        },
                        _b[theme.breakpoints.down("sm")] = {
                            fontSize: ".7rem"
                        },
                        _b)
                } })),
            modeSwitcher: (React__default.createElement(ModeSwitcher, { display: displayModes, value: displayMode, onChange: setDisplayMode, masonryViewSize: masonryViewSize, setMasonryViewSize: setMasonryViewSize, KIcon: KIcon })),
            sx: { minHeight: function (theme) { return theme.spacing(7); } },
            children: (enabledActions === null || enabledActions === void 0 ? void 0 : enabledActions.length) > 0 ? (React__default.createElement(Actions, { enabledActions: enabledActions, selectedElements: selected, onActionClick: handleActionClick, displayType: ActionDisplayType.BUTTON, isSelection: true, KIcon: KIcon, shareTokenDownloadAuthorized: shareTokenDownloadAuthorized, contactSheetsEnabled: contactSheetsEnabled, actionButtonsColor: actionButtonsColor })) : null
        }, Node: ActionsBarLayout }));
    if (!sticky) {
        return (React__default.createElement(Box, { "data-testid": "ActionsBarContainer", className: clsx(
            // for integration and tests
            "ActionsBarContainer"), sx: {
                position: "relative",
                backgroundColor: "background.paper"
            } }, ActionBarComponent));
    }
    return (React__default.createElement(Box, { "data-testid": "ActionsBarContainer", className: clsx(
        // for integration and tests
        "ActionsBarContainer", shouldAnimate && topIspassed && "ActionsBarContainer-topIsPassed", shouldAnimate && "ActionsBarContainer-scrollDirection-".concat(scrollDirection)), sx: {
            position: "relative",
            backgroundColor: "background.paper",
            "& .ActionsBarContainerAnimated": {
                left: 0,
                width: "100%"
            }
        } },
        React__default.createElement(Box, { "data-testid": "ActionsBarContainerDummy", ref: refDummy, style: { height: actionsBarHeight }, className: clsx(
            // for integration and tests
            "ActionsBarContainerDummy", topIspassed && "ActionsBarContainerDummy-passed") }),
        React__default.createElement(animated.div, { style: __assign(__assign({}, animatedStyles), { position: shouldAnimate && topIspassed ? "fixed" : "absolute", borderBottomStyle: "solid", 
                // Go over basket sidepanel only when animated
                zIndex: shouldAnimate && topIspassed ? 2 : 1, boxShadow: shouldAnimate && topIspassed && scrollDirection === ScrollTriggerDirection.Up
                    ? theme.shadows[5]
                    : "none" }), "data-testid": "ActionsBarContainerAnimated", className: clsx(
            // for integration and tests
            "ActionsBarContainerAnimated", "ActionsBarContainerAnimated-position-".concat(shouldAnimate && topIspassed ? "fixed" : "absolute")), ref: refActionsBar }, ActionBarComponent)));
};
var DataviewActionsBar = function (_a) {
    var sortList = _a.sortList, enabledActions = _a.enabledActions, actionButtonsColor = _a.actionButtonsColor, displayCount = _a.displayCount, displayModes = _a.displayModes, defaultDisplayMode = _a.defaultDisplayMode, setSelectionToContactSheets = _a.setSelectionToContactSheets, setSelectionToInsert = _a.setSelectionToInsert, KIcon = _a.KIcon, shareTokenDownloadAuthorized = _a.shareTokenDownloadAuthorized, contactSheetsEnabled = _a.contactSheetsEnabled, GetCustomerAugmentedComponent = _a.GetCustomerAugmentedComponent, allowSelection = _a.allowSelection, sticky = _a.sticky, dataViewKey = _a.dataViewKey, _b = _a.displayFilterToggler, displayFilterToggler = _b === void 0 ? true : _b;
    // Avoid calling hooks too much during animation
    var setSelectionToAdd = useSetRecoilState(addToBasketSelection);
    var selected = useRecoilValue(selectedElements(dataViewKey));
    var _c = useRecoilState(dataViewDisplayMode(dataViewKey)), displayMode = _c[0], setDisplayMode = _c[1];
    var _d = useRecoilState(dataViewMasonryDisplaySize(dataViewKey)), masonryViewSize = _d[0], setMasonryViewSize = _d[1];
    var _e = useRecoilState(dataViewSortSelector(dataViewKey)), sort = _e[0], setSort = _e[1];
    var setSelectionToDownloadManager = useSetRecoilState(downloadManagerSelectionSelector(dataViewKey));
    useEffect(function () {
        if (defaultDisplayMode &&
            (!displayMode || (displayModes && !displayModes.includes(displayMode)))) {
            if (defaultDisplayMode && (displayModes === null || displayModes === void 0 ? void 0 : displayModes.includes(defaultDisplayMode))) {
                setDisplayMode(defaultDisplayMode);
            }
            else {
                if (displayModes) {
                    setDisplayMode(displayModes[0]);
                }
            }
        }
    }, [defaultDisplayMode, displayMode, displayModes, setDisplayMode]);
    return (React__default.createElement(DataviewActionsBarMemo, { setSelectionToAdd: setSelectionToAdd, selected: selected, displayMode: displayMode !== null && displayMode !== void 0 ? displayMode : (displayModes && displayModes.length > 0 ? displayModes[0] : SpecificDataviewType.MOSAIC), setDisplayMode: setDisplayMode, masonryViewSize: masonryViewSize, setMasonryViewSize: setMasonryViewSize, sort: sort, setSort: setSort, sortList: sortList, enabledActions: enabledActions, actionButtonsColor: actionButtonsColor, displayCount: displayCount, displayModes: displayModes, setSelectionToDownloadManager: setSelectionToDownloadManager, setSelectionToContactSheets: setSelectionToContactSheets, setSelectionToInsert: setSelectionToInsert, KIcon: KIcon, shareTokenDownloadAuthorized: shareTokenDownloadAuthorized, contactSheetsEnabled: contactSheetsEnabled, GetCustomerAugmentedComponent: GetCustomerAugmentedComponent, allowSelection: allowSelection, sticky: sticky, dataViewKey: dataViewKey, displayFilterToggler: displayFilterToggler }));
};

export { DataviewActionsBar as default };
//# sourceMappingURL=index.js.map
